// src/utils/generateBlogImage.ts
import React from 'react';

export interface ThemeConfig {
  name: string;
  bgStart: string;
  bgMid: string;
  bgEnd: string;
  primary: string;
  secondary: string;
  glow1: string;
  glow2: string;
  pillBg: string;
  pillBorder: string;
  pillText: string;
  accentText: string;
}

export const BLOG_THEMES: ThemeConfig[] = [
  {
    name: 'Cosmic Violet',
    bgStart: '#090514',
    bgMid: '#160d2e',
    bgEnd: '#241047',
    primary: '#a855f7',
    secondary: '#6366f1',
    glow1: '#9333ea',
    glow2: '#4f46e5',
    pillBg: 'rgba(168, 85, 247, 0.16)',
    pillBorder: 'rgba(168, 85, 247, 0.4)',
    pillText: '#e9d5ff',
    accentText: '#c084fc',
  },
  {
    name: 'Electric Cyan',
    bgStart: '#020b14',
    bgMid: '#061a2f',
    bgEnd: '#0d3259',
    primary: '#38bdf8',
    secondary: '#06b6d4',
    glow1: '#0284c7',
    glow2: '#0891b2',
    pillBg: 'rgba(56, 189, 248, 0.16)',
    pillBorder: 'rgba(56, 189, 248, 0.4)',
    pillText: '#bae6fd',
    accentText: '#38bdf8',
  },
  {
    name: 'Emerald Matrix',
    bgStart: '#02120d',
    bgMid: '#05291d',
    bgEnd: '#094330',
    primary: '#34d399',
    secondary: '#10b981',
    glow1: '#059669',
    glow2: '#047857',
    pillBg: 'rgba(52, 211, 153, 0.16)',
    pillBorder: 'rgba(52, 211, 153, 0.4)',
    pillText: '#a7f3d0',
    accentText: '#34d399',
  },
  {
    name: 'Sunset Magenta',
    bgStart: '#140411',
    bgMid: '#2b0925',
    bgEnd: '#4c0d3a',
    primary: '#f472b6',
    secondary: '#e11d48',
    glow1: '#db2777',
    glow2: '#9333ea',
    pillBg: 'rgba(244, 114, 182, 0.16)',
    pillBorder: 'rgba(244, 114, 182, 0.4)',
    pillText: '#fbcfe8',
    accentText: '#f472b6',
  },
  {
    name: 'Amber Solar',
    bgStart: '#140902',
    bgMid: '#2e1505',
    bgEnd: '#4a2408',
    primary: '#fb923c',
    secondary: '#f59e0b',
    glow1: '#ea580c',
    glow2: '#d97706',
    pillBg: 'rgba(251, 146, 60, 0.16)',
    pillBorder: 'rgba(251, 146, 60, 0.4)',
    pillText: '#fed7aa',
    accentText: '#fb923c',
  },
  {
    name: 'Midnight Indigo',
    bgStart: '#070a14',
    bgMid: '#0f172a',
    bgEnd: '#1e293b',
    primary: '#818cf8',
    secondary: '#6366f1',
    glow1: '#4f46e5',
    glow2: '#38bdf8',
    pillBg: 'rgba(129, 140, 248, 0.16)',
    pillBorder: 'rgba(129, 140, 248, 0.4)',
    pillText: '#c7d2fe',
    accentText: '#a5b4fc',
  },
];

/**
 * Deterministically picks a theme based on a string hash
 */
export function getBlogTheme(text: string): ThemeConfig {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash << 5) - hash + text.charCodeAt(i);
    hash |= 0;
  }
  return BLOG_THEMES[Math.abs(hash) % BLOG_THEMES.length];
}

/**
 * XML escape helper for safe SVG rendering
 */
export function escapeXml(str: string): string {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Word wrapper with typographic orphan prevention
 */
export function wrapBlogTitle(text: string, maxCharsPerLine = 32, maxLines = 4): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];

  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    if ((currentLine + (currentLine ? ' ' : '') + word).length <= maxCharsPerLine) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
      if (lines.length === maxLines - 1) break;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  // Prevent typographic orphans on the last line
  if (lines.length > 1) {
    const lastWords = lines[lines.length - 1].split(/\s+/);
    const prevWords = lines[lines.length - 2].split(/\s+/);
    if (lastWords.length === 1 && prevWords.length >= 3) {
      const moved = prevWords.pop();
      if (moved) {
        lastWords.unshift(moved);
        lines[lines.length - 2] = prevWords.join(' ');
        lines[lines.length - 1] = lastWords.join(' ');
      }
    }
  }

  if (lines.length > maxLines) {
    lines.length = maxLines;
    lines[maxLines - 1] += '...';
  }

  return lines;
}

/**
 * Generates an SVG string representation of a high-end blog post cover featuring the title
 */
export function generateBlogTitleSvg(title: string, category = 'TooliQ AI Insights'): string {
  const safeTitle = title || 'AI Tools & Technology Guide';
  const theme = getBlogTheme(safeTitle);
  const lines = wrapBlogTitle(safeTitle, 32, 4);

  let fontSize = 46;
  let lineHeight = 60;
  let startY = 260;

  if (lines.length === 1) {
    fontSize = 56;
    lineHeight = 72;
    startY = 320;
  } else if (lines.length === 2) {
    fontSize = 50;
    lineHeight = 66;
    startY = 290;
  } else if (lines.length === 3) {
    fontSize = 44;
    lineHeight = 58;
    startY = 260;
  } else {
    fontSize = 38;
    lineHeight = 50;
    startY = 240;
  }

  const categoryLabel = escapeXml(category || 'TooliQ AI Insights');
  const badgeWidth = Math.min(320, Math.max(160, categoryLabel.length * 9.2 + 50));

  const tspanLines = lines
    .map(
      (line, i) =>
        `<tspan x="110" dy="${i === 0 ? 0 : lineHeight}">${escapeXml(line)}</tspan>`
    )
    .join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 675" width="1200" height="675">
  <defs>
    <!-- Background Gradient -->
    <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="${theme.bgStart}" />
      <stop offset="50%" stop-color="${theme.bgMid}" />
      <stop offset="100%" stop-color="${theme.bgEnd}" />
    </linearGradient>

    <!-- Glowing Orbs -->
    <radialGradient id="glowTopRight" cx="90%" cy="15%" r="65%">
      <stop offset="0%" stop-color="${theme.glow1}" stop-opacity="0.38" />
      <stop offset="60%" stop-color="${theme.glow1}" stop-opacity="0.08" />
      <stop offset="100%" stop-color="${theme.glow1}" stop-opacity="0" />
    </radialGradient>

    <radialGradient id="glowBottomLeft" cx="10%" cy="85%" r="60%">
      <stop offset="0%" stop-color="${theme.glow2}" stop-opacity="0.3" />
      <stop offset="55%" stop-color="${theme.glow2}" stop-opacity="0.06" />
      <stop offset="100%" stop-color="${theme.glow2}" stop-opacity="0" />
    </radialGradient>

    <!-- Tech Grid Overlay -->
    <pattern id="gridPattern" width="48" height="48" patternUnits="userSpaceOnUse">
      <path d="M 48 0 L 0 0 0 48" fill="none" stroke="rgba(255, 255, 255, 0.035)" stroke-width="1" />
      <circle cx="48" cy="0" r="1.5" fill="rgba(255, 255, 255, 0.08)" />
    </pattern>

    <!-- Border Gradient -->
    <linearGradient id="borderGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="rgba(255, 255, 255, 0.22)" />
      <stop offset="40%" stop-color="rgba(255, 255, 255, 0.06)" />
      <stop offset="100%" stop-color="${theme.primary}" stop-opacity="0.35" />
    </linearGradient>

    <!-- Text Shadow Filter -->
    <filter id="textGlow" x="-10%" y="-10%" width="120%" height="120%">
      <feDropShadow dx="0" dy="4" stdDeviation="12" flood-color="#000000" flood-opacity="0.6" />
    </filter>

    <filter id="cardShadow" x="-5%" y="-5%" width="110%" height="110%">
      <feDropShadow dx="0" dy="12" stdDeviation="24" flood-color="#000000" flood-opacity="0.5" />
    </filter>
  </defs>

  <!-- Background Base -->
  <rect width="1200" height="675" fill="url(#bgGrad)" />
  <rect width="1200" height="675" fill="url(#glowTopRight)" />
  <rect width="1200" height="675" fill="url(#glowBottomLeft)" />
  <rect width="1200" height="675" fill="url(#gridPattern)" />

  <!-- Accent Sparkles -->
  <g fill="${theme.primary}" opacity="0.4">
    <circle cx="1060" cy="180" r="3" />
    <circle cx="980" cy="240" r="2" />
    <circle cx="1120" cy="300" r="2.5" />
    <circle cx="140" cy="480" r="2" />
    <circle cx="220" cy="540" r="3" />
  </g>

  <!-- Modern Inset Card -->
  <rect x="55" y="55" width="1090" height="565" rx="28" fill="rgba(255, 255, 255, 0.02)" stroke="url(#borderGrad)" stroke-width="1.5" filter="url(#cardShadow)" />

  <!-- Top Navigation & Badges -->
  <g transform="translate(100, 105)">
    <!-- Brand Logo Mark -->
    <rect x="0" y="0" width="38" height="38" rx="10" fill="url(#bgGrad)" stroke="${theme.primary}" stroke-width="1.5" />
    <path d="M 12 19 L 19 12 L 26 19 L 19 26 Z" fill="${theme.primary}" />
    <circle cx="19" cy="19" r="3" fill="#ffffff" />

    <text x="50" y="25" font-family="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" font-size="18" font-weight="800" fill="#ffffff" letter-spacing="0.05em">
      TOOLIQ<tspan fill="${theme.primary}">.AI</tspan>
    </text>

    <!-- Category Pill -->
    <g transform="translate(200, 0)">
      <rect x="0" y="0" width="${badgeWidth}" height="38" rx="19" fill="${theme.pillBg}" stroke="${theme.pillBorder}" stroke-width="1.2" />
      <circle cx="20" cy="19" r="4.5" fill="${theme.primary}" />
      <text x="34" y="24" font-family="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" font-size="13.5" font-weight="700" fill="${theme.pillText}" letter-spacing="0.03em">
        ${categoryLabel}
      </text>
    </g>
  </g>

  <!-- Main Article Title -->
  <text x="110" y="${startY}" font-family="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif" font-size="${fontSize}" font-weight="800" fill="#ffffff" letter-spacing="-0.025em" filter="url(#textGlow)">
    ${tspanLines}
  </text>

  <!-- Bottom Divider Line -->
  <line x1="100" y1="525" x2="1100" y2="525" stroke="rgba(255, 255, 255, 0.1)" stroke-width="1" />

  <!-- Footer Elements -->
  <g transform="translate(100, 565)">
    <text x="0" y="0" font-family="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" font-size="14" font-weight="500" fill="rgba(255, 255, 255, 0.6)">
      tooliq.ai • Expert Artificial Intelligence Guide
    </text>
  </g>

  <g transform="translate(1100, 565)">
    <text x="0" y="0" text-anchor="end" font-family="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" font-size="14" font-weight="700" fill="${theme.accentText}" letter-spacing="0.02em">
      Read Full Article →
    </text>
  </g>
</svg>`;
}

/**
 * Generates a Data URL for the blog title SVG
 */
export function generateBlogTitleSvgDataUrl(title: string, category?: string): string {
  const svg = generateBlogTitleSvg(title, category);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Returns either the provided image URL or generates the title-based fallback
 */
export function getBlogImageUrl(image?: string, title?: string, category?: string): string {
  if (image && image.trim().length > 0) {
    return image;
  }
  return generateBlogTitleSvgDataUrl(title || 'AI Article', category);
}

/**
 * Fallback event handler for <img> onError
 */
export function handleBlogImageError(
  e: React.SyntheticEvent<HTMLImageElement, Event>,
  title: string,
  category?: string
): void {
  const target = e.currentTarget;
  if (target.dataset.hasFallback !== 'true') {
    target.dataset.hasFallback = 'true';
    target.src = generateBlogTitleSvgDataUrl(title, category);
  }
}
